// Recreate templates — frozen wizard states the user can drop their product
// into without going through the full wizard.
//
// Each template captures everything /api/generate (and downstream text-overlay
// rendering) needs to reproduce a finished post visual, minus the product
// itself. The user clicks a tile on /create, uploads their product on the
// /appv2/recreate/<id> page, and the runtime calls /api/generate with the
// template's frozen settings — same API surface as the wizard.
//
// Data lives in a sibling JSON file (`recreate-templates.json`) so the
// dev-only "Save as Recreate template" button in /appv2 can write to it
// directly via the Vite middleware at /api/recreate-templates-save. This
// module is just typing + lookup helpers.
//
// Authoring path: complete a post through the wizard on /appv2, click
// "Save as Recreate template (dev)" on the final step, supply a name +
// id slug in the prompt. The button captures the current full-resolution
// composited image as the preview PNG and serializes the wizard state
// into the JSON. Commit the new JSON + PNG and ship.
//
// Preview images live at /public/recreate-previews/<id>.png — the same
// pattern as /public/category-previews/. The dev button writes there
// automatically; hand-authored templates need to drop a PNG manually.

import type { PostType } from '@/pages/app/components/StepCategory';
import type { SocialDesignId } from '@/pages/app/components/StepSocialDesign';
import type { Provider, Quality } from '@/lib/api';
import type { TextOverlay } from '@/pages/app/lib/composeRender';

// Vite imports JSON as the parsed array at build time. In dev, the Vite
// middleware mutates the file in place; HMR refreshes consumers when the
// JSON changes on disk so newly-saved templates appear immediately.
import recreateTemplatesData from './recreate-templates.json';


export interface RecreateTemplate {
  /** Stable URL slug. Used in /appv2/recreate/<id>. */
  id: string;
  /** Human-readable name shown on the tile + result page. */
  name: string;
  /** Drives the filter chip on /create. Maps to the wizard's PostType. */
  category: PostType;

  // ---------- Frozen wizard state ----------
  /** Platform ID from socialPlatforms[] / productShotFormats[]. Drives the
   *  aspect ratio + dimensions that get passed to /api/generate. */
  platformId: string;
  /** Backend template id. For product-shot/carousel: a scene id like
   *  'template5'. For fashion: a fashion model id like 'model-3'. For
   *  social: 'social-bg-<designId>'. */
  templateId: string;
  /** Per-template quality lock. Drives credit cost on Recreate. Mix tiers
   *  across templates to balance margin (Low = 1cr, Medium = 2cr, High = 4cr). */
  quality: Quality;
  /** Image-gen provider. Defaults to 'openai' if absent. */
  provider?: Provider;

  // ---------- Per-category specifics ----------
  /** Fashion route only. Tells the mask-based pipeline which region to swap. */
  garmentType?: 'top' | 'bottoms' | 'shoes';
  /** Social Post route only. Identifies the JSON/React-component design. */
  socialDesignId?: SocialDesignId;
  /** Optional hex passed through to social-bg-* compose path. */
  bgHex?: string;

  // ---------- Text styling ----------
  /** Locked text-overlay positions + fonts + colors. Phase 4 polish will run
   *  /api/suggest-copy against the user's product and substitute role-based
   *  slots so the layout stays but the words match the product.
   *
   *  Not applicable to Social Post category — that uses SocialPostSlots
   *  driven by the design's React component instead. */
  textOverlays?: TextOverlay[];

  // ---------- UI metadata ----------
  /** Path under /public — preview tile shown on the /create grid. */
  previewImage: string;
  /** Optional tone hint for the future Phase 4 copy regeneration. */
  copyHint?: string;
}


// Cast the JSON-imported array to the strict TS type. The JSON is hand-
// written by the dev button so we trust it; runtime guards aren't worth
// the noise. If the JSON is malformed, TS won't catch it but render will
// fail visibly.
export const recreateTemplates: RecreateTemplate[] =
  recreateTemplatesData as unknown as RecreateTemplate[];


/** Lookup helper — returns the template or undefined if not found. */
export function getRecreateTemplate(id: string | undefined): RecreateTemplate | undefined {
  if (!id) return undefined;
  return recreateTemplates.find((t) => t.id === id);
}


/** Category-friendly label for tile badges + filter chips.
 *  'In Hand' is the locked user-facing name for the product-swap route
 *  (a model holding the product) — was "Product Swap" on /create and
 *  "Held Product" on /appv3 before the Jun 11 rename. */
export const RECREATE_CATEGORY_LABEL: Record<PostType, string> = {
  'product-shot': 'Product Shot',
  'product-swap': 'In Hand',
  'fashion': 'Fashion',
  'carousel': 'Carousel',
  'single': 'Social Post',
};
